import device from 'current-device';
import { trafficSourceInfo } from 'traffic-source-analyzer';
import { initAnalytics } from './analytics/analytics';
import { initAds } from './ads/ads';
import { initRealUserMonitoring } from './realUserMonitoring/realUserMonitoring';
import {
  formatDate,
  getAnalyticsConsent,
  getGdprApplies, getShouldNotGooglePersonalizeAds, readGooglePersonalizationCookie,
  readPublisherConsentCookie,
  readVendorsConsentCookie,
  removeNullParams, updateGdprApplies,
} from './init.utils';
import { getPageURL } from './pageURL/pageURL';
import { markPerformance } from './performance/index';
import { getIdentifier } from './userIdentifier';
import { ErrorReporter } from './errorReporter.type';
import { AdParams, AdSlots } from './ads/ads.types';
import { AnalyticsParams } from './analytics/analytics.types';

export interface StateAdParams {
  language: string
  category: string | null
  pageType: string
  articleId?: string
  articleTemplate?: Array<string>
  channels?: Array<string>
  contentTags?: Array<string>
  adSlots: AdSlots
  headerBidderPublisherID: string
  mainMedia?: string
  commercialTags?: Array<string>
  dfpCategory?: string
  dfpProperty?: string
}

export interface StateAnalyticsParams {
  category: string | null
  articleAuthor?: string
  articleTitle?: string
  articlePublicationTime?: string
  property?: string
  countryCode: string
  articleContentCount?: {[key:string]: number}
  articleWordCount?: number
}

export type CoreParams = StateAdParams & StateAnalyticsParams;

const pageURL = getPageURL();
const monitoringEndpoint = 'https://rum.minutemediaservices.com/';

export const createAdParameters = ({
  language,
  category,
  pageType,
  articleId,
  articleTemplate,
  channels,
  contentTags,
  mainMedia,
  commercialTags,
  dfpProperty,
}: StateAdParams) => removeNullParams({
  language,
  platform: device.type,
  category,
  contentTags,
  pageType,
  test: pageURL.test,
  affiliateId: pageURL.affiliateId,
  articleId,
  articleTemplate,
  distributionChannels: channels,
  mmUserIdentifier: getIdentifier(),
  trafficSource: trafficSourceInfo.trafficSource,
  trafficSourceAndId: trafficSourceInfo.trafficSourceAndId,
  mainMedia,
  commercialTags,
  dfpProperty,
});

export const createAnalyticsParametersWithAdParams = (adParameters: AdParams, analyticsParams: AnalyticsParams) => ({
  ...adParameters,
  ...analyticsParams,
});

export const createAnalyticsParams = ({ category, articleAuthor, articleTitle, articlePublicationTime, property, countryCode, articleContentCount, articleWordCount }: StateAnalyticsParams) => ({
  pageIdentifier: category,
  geoDetected: countryCode,
  GA_event_cookieConsent: readVendorsConsentCookie(),
  analyticsConsent: getAnalyticsConsent(readPublisherConsentCookie()),
  markupFormat: 'web',
  gdprApplies: getGdprApplies(),
  trafficReferrer: document.referrer,
  trafficId: trafficSourceInfo.trafficId,
  articleAuthor,
  articleTitle,
  articlePublicationTime: articlePublicationTime ? formatDate(new Date(articlePublicationTime)) : '',
  property,
  articleContentCount,
  articleWordCount,
});

export function init(params: CoreParams, errorProvider: ErrorReporter) {
  const { pageType, adSlots, headerBidderPublisherID, countryCode, category, dfpCategory } = params;
  const adParameters = createAdParameters(params);
  const adsCategory = dfpCategory || category;
  const shouldNotPersonalizeAds = getShouldNotGooglePersonalizeAds(readGooglePersonalizationCookie());

  initRealUserMonitoring(countryCode, pageType, monitoringEndpoint, errorProvider);

  try {
    initAnalytics(createAnalyticsParametersWithAdParams(adParameters, createAnalyticsParams(params)));
    markPerformance('init-analytics');
  } catch (e) {
    errorProvider.reportException(e);
  }
  try {
    initAds(adSlots, adParameters, adsCategory!, headerBidderPublisherID, shouldNotPersonalizeAds, errorProvider);
    markPerformance('init-ads');
  } catch (e) {
    errorProvider.reportException(e);
  }
  try {
    updateGdprApplies();
  } catch (e) {
    errorProvider.reportException(e);
  }
}
